/* Keyboard containment for dialogs.

   Three obligations, all of which a modal that only renders a scrim fails:
   focus must move into the dialog on open, Tab must cycle inside it, and focus
   must return to whatever opened it on close — otherwise a keyboard user lands
   back at the top of the document and has to navigate the whole page again.

   Escape is handled here too rather than in each dialog, so every overlay in
   the app closes the same way. */

import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  onEscape?: () => void,
): void {
  /* Held in a ref, and this is the load-bearing part rather than a tidiness
     one. Every dialog in the app passes an inline `onClose={() => …}`, so the
     callback is a new identity on each render. With it in the dependency array
     the whole effect tore down and re-ran on **every keystroke** in any sheet
     containing a field: the cleanup handed focus back to the opener, and the
     new run's rAF moved it to the first focusable element in the panel — the
     close button, because it sits in the header above the body.

     It looked like "type a word and the caret jumps to the cross" because each
     keystroke cancelled the previous frame's callback, so the jump only landed
     once typing paused long enough for a frame to pass. Everything typed after
     that went into the close button and was lost. */
  const escapeRef = useRef(onEscape)
  useEffect(() => {
    escapeRef.current = onEscape
  }, [onEscape])

  useEffect(() => {
    if (!active) return
    const node = ref.current
    if (!node) return

    const opener = document.activeElement as HTMLElement | null

    /* Deferred a frame: on open the dialog is still animating in from its
       initial transform, and focusing during that frame makes some browsers
       scroll the transformed element into view — which drags the page. */
    const focusId = requestAnimationFrame(() => {
      const first = node.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? node).focus({ preventScroll: true })
    })

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && escapeRef.current) {
        e.stopPropagation()
        escapeRef.current()
        return
      }
      if (e.key !== 'Tab') return

      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]!
      const last = items[items.length - 1]!

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      cancelAnimationFrame(focusId)
      document.removeEventListener('keydown', onKeyDown, true)
      // `isConnected` guards the case where the opener itself was removed —
      // focusing a detached node silently sends focus to <body>.
      if (opener?.isConnected) opener.focus({ preventScroll: true })
    }
    // `onEscape` is deliberately absent: it is read through the ref above, and
    // depending on it here is the bug described there.
  }, [ref, active])
}
